import type { AnalyticDateFilter } from '~/presentation/ui';
import type { IPageAnalyticsForFilter } from '../types';

import { LISTING_METRICS, LISTING_DIMENSIONS, LISTING_ORDER_BY_SESSIONS_DESC } from '../constants';
import { useCustomPageAnalyticQueryV2 } from '~/modules/page-analytic/api';
import { buildPAQueryVariables } from '~/modules/page-analytic/helpers';
import { EAnalyticMode } from '~/modules/analytics/types';
import { captureSentryError } from '~/exceptions/sentry';
import { mapListingRowsToPageAnalytics } from '../utils';
import { useMemo } from 'react';

export interface IFetchPageAnalyticsForFilterProps {
  currentDates: AnalyticDateFilter;
  mode?: EAnalyticMode;
  enabled?: boolean;
  locationPath?: string;
}

interface IFetchPageAnalyticsForFilterResult {
  data: IPageAnalyticsForFilter[];
  isLoading: boolean;
  refetch: () => void;
}

export const useFetchPageAnalyticsForFilter = ({
  currentDates,
  mode = EAnalyticMode.ALL_SESSION,
  enabled = true,
  locationPath,
}: IFetchPageAnalyticsForFilterProps): IFetchPageAnalyticsForFilterResult => {
  const variables = useMemo(
    () =>
      buildPAQueryVariables({
        mode,
        metrics: LISTING_METRICS,
        dimensions: LISTING_DIMENSIONS,
        dateRange: currentDates,
        locationPath,
        orderBy: LISTING_ORDER_BY_SESSIONS_DESC,
      }),
    [mode, currentDates, locationPath],
  );

  const { data, isFetching, refetch } = useCustomPageAnalyticQueryV2(variables, {
    enabled,
    onError: (error) => {
      captureSentryError(error, {
        source: 'useFetchPageAnalyticsForFilter',
        tags: { analytic: 'Analytic' },
      });
    },
  });

  const pages = useMemo(() => mapListingRowsToPageAnalytics(data), [data]);

  return { data: pages, isLoading: isFetching, refetch };
};
